import { useCallback, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { emptyFitnessWorkoutState, fitnessWorkoutController, type FitnessWorkoutState } from '@/lib/fitnessWorkoutController';
import { FitnessWorkoutRuntimeManaged } from '@/lib/fitnessWorkoutRuntimeContext';

export function useFitnessWorkoutRuntime(ownerId: string | null) {
  const runtime = useSyncExternalStore(fitnessWorkoutController.subscribe, fitnessWorkoutController.getSnapshot, () => null);
  const authIdentity = useSyncExternalStore(fitnessWorkoutController.subscribe, fitnessWorkoutController.getIdentity, fitnessWorkoutController.getIdentity);
  const managed = useContext(FitnessWorkoutRuntimeManaged);
  const [empty] = useState(emptyFitnessWorkoutState);
  useEffect(() => {
    // Isolated Workout mounts used by module consumers/tests own their route
    // identity. The production app's provider alone binds the auth owner.
    if (!managed) fitnessWorkoutController.bindOwner(ownerId);
    if (ownerId && fitnessWorkoutController.getOwner() === ownerId && !fitnessWorkoutController.getOwned(ownerId)) fitnessWorkoutController.start(ownerId);
  }, [ownerId, managed, authIdentity.epoch]);
  useEffect(() => {
    const routeEpoch = authIdentity.epoch;
    return () => {
    // A resume prompt or failed plan load is not a living workout. Real runs,
    // including paused runs, deliberately survive the route's subscriptions.
    const current = ownerId && fitnessWorkoutController.getOwned(ownerId);
      if (current && current.epoch === routeEpoch && current.state.exercises.length === 0) fitnessWorkoutController.retire();
    };
  }, [ownerId, authIdentity.epoch]);
  const owned = runtime?.ownerId === ownerId ? runtime : null;
  const expectedRunId = owned?.runId, expectedEpoch = owned?.epoch;
  const field = useCallback(<K extends keyof FitnessWorkoutState,>(key: K, update: FitnessWorkoutState[K] | ((before: FitnessWorkoutState[K]) => FitnessWorkoutState[K])) => {
    const current = ownerId && fitnessWorkoutController.getOwned(ownerId);
    if (!current || !expectedRunId || current.runId !== expectedRunId || current.epoch !== expectedEpoch) return;
    fitnessWorkoutController.patch(ownerId!, state => ({ [key]: typeof update === 'function' ? (update as (before: FitnessWorkoutState[K]) => FitnessWorkoutState[K])(state[key]) : update }));
  }, [ownerId, expectedRunId, expectedEpoch]);
  return { runtime: owned, state: owned?.state ?? empty, field };
}
